import { Role, RolePermissions } from '../types/models';
import { settingsService } from '../services/settings.service';
import { CompanySettings, SettingsAccessConfig, SettingsClientCache } from '../types/settings';
import {
  readPlainVersionId,
  readSettingsClientCache,
  saveSettingsClientCache,
} from '../utils/settingsSecureStorage';
import { applyCachedDocumentBranding } from '../utils/settingsBranding';
import { saveRequestPacing } from '../utils/requestPacing';
import { setModuleFlags } from './moduleAccess';
import { setPosPolicy } from './posPolicy';
import { RouteAccessRule, setRouteAccessConfig } from './routeAccess';

export function applyAccessConfig(config: SettingsAccessConfig): void {
  const rules: RouteAccessRule[] = [...config.routeAccessRules].map((r) => ({
    prefix: r.prefix,
    allowed: [...r.allowed] as Role[],
  }));
  rules.sort((a, b) => b.prefix.length - a.prefix.length);
  const adminOnly = [...config.adminOnlyPrefixes].sort((a, b) => b.length - a.length);
  setRouteAccessConfig(rules, adminOnly);
  setModuleFlags({
    isZeettuEnabled: config.isZeettuEnabled,
    isInstallmentEnabled: config.isInstallmentEnabled,
    isPOSEnabled: config.isPOSEnabled,
    isBDenabled: config.isBDenabled,
  });
  setPosPolicy(config);
  saveRequestPacing(config.artificialDelay);
}

function rolePermissionsFromCache(cache: SettingsClientCache | null): RolePermissions[] {
  return cache?.access.rolePermissions ?? [];
}

async function fetchAndPersistCache(isAdminOrOffice: boolean): Promise<SettingsClientCache> {
  const access = await settingsService.getAccessConfig();
  const versionId = access.versionId ?? '';
  let companyDetails = undefined;
  let legalPolicies = undefined;
  if (isAdminOrOffice) {
    const settings = await settingsService.getSettings();
    companyDetails = settings.companyDetails;
    legalPolicies = settings.legalPolicies;
  }
  const logos = access.logos ?? {};
  const cache: SettingsClientCache = {
    versionId,
    access,
    logos,
    companyDetails:
      companyDetails ??
      (access.tradingName || access.legalName
        ? {
            tradingName: access.tradingName,
            legalName: access.legalName,
          }
        : undefined),
    legalPolicies,
    penaltyFee: access.penaltyFee,
  };
  await saveSettingsClientCache(cache);
  return cache;
}

/**
 * Compare local vs remote settings version, refresh encrypted cache when stale,
 * and apply route access before the app shell renders.
 */
export async function syncSettingsAfterAuth(isAdminOrOffice: boolean): Promise<RolePermissions[]> {
  try {
    const { versionId: remoteVersion } = await settingsService.getSettingsVersion();
    const localVersion = readPlainVersionId();
    const encryptedCache = await readSettingsClientCache();

    if (localVersion === remoteVersion && encryptedCache?.access) {
      applyAccessConfig(encryptedCache.access);
      await applyCachedDocumentBranding();
      return rolePermissionsFromCache(encryptedCache);
    }

    const cache = await fetchAndPersistCache(isAdminOrOffice);
    applyAccessConfig(cache.access);
    await applyCachedDocumentBranding();
    return rolePermissionsFromCache(cache);
  } catch (err) {
    console.warn('[settingsAccess] Settings sync failed, using cache or defaults:', err);
    const cached = await readSettingsClientCache();
    if (cached?.access) {
      applyAccessConfig(cached.access);
      await applyCachedDocumentBranding();
      return rolePermissionsFromCache(cached);
    }
    setRouteAccessConfig(null, null);
    return [];
  }
}

export async function applySettingsUpdate(settings: CompanySettings): Promise<void> {
  await settingsService.persistClientCacheFromSettings(settings);
  applyAccessConfig({
    versionId: settings.versionId,
    logos: settings.logos ?? {},
    isZeettuEnabled: settings.isZeettuEnabled,
    isInstallmentEnabled: settings.isInstallmentEnabled,
    isPOSEnabled: settings.isPOSEnabled,
    isBDenabled: settings.isBDenabled,
    posBlindShiftClose: settings.posBlindShiftClose,
    posIdleLockMinutes: settings.posIdleLockMinutes,
    posVarianceNoteThreshold: settings.posVarianceNoteThreshold,
    artificialDelay: settings.artificialDelay,
    routeAccessRules: settings.routeAccessRules ?? [],
    adminOnlyPrefixes: settings.adminOnlyPrefixes ?? [],
    rolePermissions: settings.rolePermissions ?? [],
  });
  const { applyDocumentBranding } = await import('../utils/settingsBranding');
  const title =
    settings.companyDetails?.tradingName?.trim() ||
    settings.companyDetails?.legalName?.trim();
  applyDocumentBranding(settings.logos ?? {}, title);
}

/** @deprecated Use syncSettingsAfterAuth */
export async function loadAndApplyAccessConfig(): Promise<void> {
  await syncSettingsAfterAuth(false);
}

export async function refreshAccessConfig(isAdminOrOffice = true): Promise<void> {
  const cache = await fetchAndPersistCache(isAdminOrOffice);
  applyAccessConfig(cache.access);
}
